import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input
} from '@repo/ui'
import { useForm } from '@tanstack/react-form'
import { Link, createFileRoute } from '@tanstack/react-router'
import * as Sentry from '@sentry/react'
import { useState } from 'react'
import { z } from 'zod'
import { apiSendEmailVerification } from '@/api/user/emailVerification'
import { zodFieldValidator } from '@/common/parseDataType'

const emailFieldSchema = z.email('Please enter a valid email address')

export const Route = createFileRoute('/user/resend-verification')({
  validateSearch: z.object({
    email: z.string().optional().catch(''),
    error: z.string().optional().catch('')
  }),
  head: () => ({ meta: [{ title: 'Resend Verification - Snowvault' }] }),
  component: ResendVerification
})

function ResendVerification() {
  const { email: emailParam, error: errorParam } = Route.useSearch()
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const form = useForm({
    defaultValues: {
      email: emailParam ?? ''
    },
    onSubmit: async ({ value }) => {
      setError(undefined)
      try {
        await apiSendEmailVerification(value.email)
        setSuccess(true)
      } catch (err) {
        Sentry.captureException(err)
        console.error('Resend verification error:', err)
        setError(err instanceof Error ? err.message : 'Failed to send verification email. Please try again.')
      }
    }
  })

  if (success) {
    return (
      <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-sm">
          <Card>
            <CardHeader>
              <CardTitle>Verification email sent</CardTitle>
              <CardDescription>Check your inbox for a new verification link.</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/login" className="text-sm underline underline-offset-4 hover:no-underline">
                Back to sign in
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader>
            <CardTitle>Resend verification email</CardTitle>
            <CardDescription>Enter your email to receive a new verification link.</CardDescription>
          </CardHeader>
          <CardContent>
            {errorParam && <p className="pb-4 text-sm text-destructive">{errorParam}</p>}
            <form
              onSubmit={(e) => {
                e.preventDefault()
                form.handleSubmit()
              }}
            >
              <FieldGroup>
                <form.Field name="email" validators={{ onChange: zodFieldValidator(emailFieldSchema) }}>
                  {(field) => (
                    <Field>
                      <FieldLabel htmlFor={field.name}>Email</FieldLabel>
                      <Input
                        id={field.name}
                        name={field.name}
                        type="email"
                        placeholder="Enter your email"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                      {field.state.meta.errors.length > 0 && (
                        <FieldDescription className="text-destructive">
                          {String(field.state.meta.errors[0])}
                        </FieldDescription>
                      )}
                    </Field>
                  )}
                </form.Field>
              </FieldGroup>
              <div className="pt-4">
                <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                  {([canSubmit, isSubmitting]) => (
                    <Field>
                      <Button type="submit" disabled={!canSubmit || isSubmitting}>
                        {isSubmitting ? 'Sending...' : 'Send verification email'}
                      </Button>
                      <FieldDescription className="text-center">
                        <Link to="/login" className="underline underline-offset-4 hover:no-underline">
                          Back to sign in
                        </Link>
                      </FieldDescription>
                    </Field>
                  )}
                </form.Subscribe>
              </div>
              {error && <p className="pt-2 text-sm text-destructive">{error}</p>}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
